import DelayedCall from '../DelayedCall';
import ApplicationState from '../ApplicationState';
import SliderDots from './SliderDots';
import { COMMON_AJAX_DELAY, DEFAULT_GLIDER_CLASSES } from '../ApplicationConfig';
import { triggerCustomEvent } from '../EventTrigger';
import { sendGoogle } from '../Analytics';
import GalleryItems from '../gallery/GalleryItems';
import GalleryItemDto from '../gallery/GalleryItemDto';

if (typeof IntersectionObserver === 'undefined') {
    require('intersection-observer');
}

const Glide = require('@glidejs/glide');
const xhr = ApplicationState.axios;

/**
 * loadAllSlidesByObserver - если true, при появлении слайдера в области видимости будут загружены все слайды сразу,
 *                           а не только видимые
 */
export default class Slider
{
    protected root: HTMLElement;
    protected slider: typeof Glide;
    protected delayedCall: DelayedCall;
    protected slidesCount: number;
    protected useScrollableDots: boolean;
    protected adaptiveHeight: boolean = false;
    protected dots: SliderDots = null;
    protected loadAll: boolean;
    protected hideNavArrows: boolean;
    protected options;

    private defaultOptions = {
        adaptiveHeight: false,
        loadAllSlidesByObserver: false,
        useScrollableDots: false,
        perStep: 1,
        hideNavArrows: false,
        classes: DEFAULT_GLIDER_CLASSES,
        withShadow: false,
    };

    public constructor(root: string|Element, options = {}, immediateInit: boolean = false)
    {
        this.root = (root instanceof Element ? root : document.querySelector(root)) as HTMLElement;

        if (this.root === null) {
            return;
        }

        this.options = {...this.defaultOptions, ...options};

        let slidesContainer = this.root.getElementsByClassName('slider__slides')[0] as HTMLElement;
        this.slidesCount = slidesContainer.children.length;

        this.useScrollableDots = this.options['useScrollableDots'];
        this.loadAll = this.options['loadAllSlidesByObserver'];
        this.adaptiveHeight = this.options['adaptiveHeight'];

        this.delayedCall = new DelayedCall();
        this.slider      = new Glide(this.root.querySelector('.common-slider__glider-container'), this.options);

        if (this.adaptiveHeight) {
            this.root.classList.add('slider_stretch-items');
        }

        // this.delayedCall.execute(() => {
            this.slider.mount();
            if (this.slider.settings.useScrollableDots) {
                this.createDots();
            }
            if (this.options['withShadow']) {
                this.initialSlideState();
            }            
            this.displayControls();
            this.startFirstLoadObserving();
        // }, immediateInit ? 0 : 2000);
    }

    public update()
    {
        this.slider.update();

        if (this.adaptiveHeight) {
            this.adaptHeight();
        }

        if (this.useScrollableDots) {
            this.dots.update();
        }
    }

    public on(eventName: string, callback: Function)
    {
        if (this.root === null) {
            return;
        }

        this.root.addEventListener(eventName, e => {
            let evt = e as CustomEvent;
            callback(evt.detail);
        });
    }

    public native(): typeof Glide
    {
        return this.slider;
    }

    public getRootElement(): Element
    {
        return this.root;
    }

    public getSlides(excludeCloned: boolean = false): NodeListOf<Element>
    {
        let selector = '.slider__slide';

        if (excludeCloned) {
            selector += ':not(.' + this.options.classes.cloneSlide + ', .glide__slide--clone)';
        }

        return this.getRootElement().querySelectorAll(selector);
    }

    public getGalleryItems(): GalleryItems
    {
        let items = new GalleryItems;

        this.getSlides(true).forEach(slide => {
            let imageLink = slide.querySelector('[data-gallery]');
            let thumbnail = imageLink.getElementsByTagName('img').item(0);

            let thumbnailPath = thumbnail.getAttribute('data-src')
                ? thumbnail.getAttribute('data-src') : thumbnail.getAttribute('src');

            items.push(new GalleryItemDto(imageLink.getAttribute('data-src'), thumbnailPath));
        });

        return items;
    }

    protected appendEventListener()
    {
        this.root.addEventListener('click', e => {
            let control = e.target as HTMLElement;

            if (control.classList.contains('js-slider-control')) {
                if (control.dataset.dir === '<' || control.dataset.dir === '>') { // клик по стрелкам
                    this.slideSeveral(control.dataset.dir, this.slider.settings.perStep);
                } else { // клик по точкам
                    let selectedIndex = Number(control.dataset.dir) * this.slider.settings.perStep;
                    
                    if (selectedIndex > this.slidesCount - 1 - this.slider.settings.perStep) {
                        selectedIndex = this.slidesCount - this.slider.settings.perStep;
                    }

                    this.slider.go('=' + selectedIndex);
                }
            }
        });

        this.slider.on('run.after', move => {
            let index = this.slider.index < 0
                ? this.slidesCount - Math.abs(this.slider.index)
                : this.slider.index;

            this.loadSlides(index, this.getVisbileSlidesCount());

        });

        this.slider.on('move', () => {
            if (this.dots !== null) {
                let index = this.slider.index >= 0 ? this.slider.index : this.slidesCount - Math.abs(this.slider.index);
                index = Math.ceil(index / this.slider.settings.perStep);

                this.dots.scrollTo(index);
            }
        });

        let delayedCall = new DelayedCall();
        
        this.slider.on('resize', () => {
            delayedCall.cancelLastCall();
            delayedCall.execute(() => {
                this.loadSlides(this.slider.index, this.slider.settings.perView);
                this.displayControls();
                
                if (this.useScrollableDots) {
                    this.createDots();
                }

                if (this.adaptiveHeight) {
                    this.adaptHeight();
                }
            }, COMMON_AJAX_DELAY);
        });

        if (this.adaptiveHeight) {
            this.on('slider.after.loading', this.adaptHeight.bind(this));
        }

        let slidesWrapper = this.root.querySelector('.slider__slides');
        if (slidesWrapper) {
            slidesWrapper.addEventListener('click', e => {
                let target = e.target as HTMLElement;
                let href = target.hasAttribute('href') ? target.getAttribute('href') : null;

                if (href !== null && href !== '' && href !== '#' && href.search(/\.[\w\d_]+$/) === -1) {
                    sendGoogle('Слайдер', 'Клик', href);
                }
            });
        }
    }

    protected initialSlideState()
    {
        let slides = this.getSlides(true);

        slides.forEach((slide, index) => {
            slide.classList.remove('slider__slide_visible');
            slide.classList.add('slider__slide_invisible');
        });

        this.showOnlyVisible(slides);

        this.slider.on('move', () => {
            slides.forEach((slide, index) => {
                slide.classList.remove('slider__slide_visible');
                slide.classList.add('slider__slide_invisible');
            });

            this.showOnlyVisible(slides);
        });

    }

    protected showOnlyVisible(slides: NodeListOf<Element>) {
        for (let slideIdx = this.slider.index; slideIdx < this.slider.index + this.slider.settings.perView; slideIdx++) {
            if (slides[slideIdx] !== undefined) {
                slides[slideIdx].classList.remove('slider__slide_invisible');
                slides[slideIdx].classList.add('slider__slide_visible');
            }
        }
    }

    protected displayControls()
    {
        this.root.querySelectorAll('.js-slider-control').forEach(control => {
            if (!control.classList.contains('slider-dots__dot')) { //устанавливаем видимость отдельно для стрелок
                if (this.slider.settings.perView >= this.slidesCount || this.slider.settings.hideNavArrows) {
                    control.classList.add('common-slider__control_hidden');
                } else {
                    control.classList.remove('common-slider__control_hidden');
                }
            } else { //устанавливаем видимость отдельно для точек
                if (this.slider.settings.perView >= this.slidesCount || !this.slider.settings.useScrollableDots) {
                    control.classList.add('common-slider__control_hidden');
                } else {
                    control.classList.remove('common-slider__control_hidden');
                }                
            }
        });
    }

    protected createDots()
    {
        let dots = this.root.querySelector('[data-common-slider-role="dots"]');
        let count = Math.ceil(this.slidesCount / this.slider.settings.perStep);
        let wrapper = dots.firstElementChild as HTMLElement;

        if (this.slider.settings.perView >= this.slidesCount) {
            wrapper.innerHTML = '';
            return;
        }

        if (dots !== null && count !== wrapper.children.length) {
            this.dots = new SliderDots(dots, {
                count: count,
                scrollable: {
                    enabled: this.slider.settings.useScrollableDots,
                    visibleDots: 1,
                },
            });
            this.dots.init();

            // FIXME TODO: для исправления бага активной точки при ресайзе нужно устанавливать slider.index
            // пропорционально perStep. Иначе далее отсчет пойдет по смещенным индексам, которые не смогут совпасть
            // с индексами точек
            this.dots.scrollTo(this.getClosestDotIndex(count));
        }
    }

    protected getClosestDotIndex(count: number)
    {
        let index = this.slider.index;
        let closest = 0;

        for (let i = 0; i < count; i++) {
            let current = i * this.slider.settings.perStep;
            let next = (i + 1) * this.slider.settings.perStep;
            
            if (index >= current && index < next) {
                closest = Math.abs(index - current) <= Math.abs(index - next) ? i : i + 1;
            }
        }

        return closest;
    }

    protected slideSeveral(dir: string, count: number)
    {
        let nextIndex = dir === '>' ? this.slider.index + count : this.slider.index - count;
        let maxIndex = this.slidesCount - 1;

        if (nextIndex < (-count)) {
            nextIndex = this.slidesCount - Math.abs(nextIndex);
            this.slider.index = nextIndex + count;
            this.slider.update();
        } else if (nextIndex > -count && nextIndex < 0 && dir === '<') {
            nextIndex = 0;
        } else if (count > 1 && nextIndex < maxIndex && nextIndex + count > maxIndex && dir === '>') {
            nextIndex = maxIndex - this.slider.settings.perView + 1;
        } else if (nextIndex > maxIndex) {
            nextIndex = nextIndex - this.slidesCount;
            this.slider.index = nextIndex - count;
            this.slider.update();
        }

        this.slider.go("=" + nextIndex);
    }

    /**
     * Загрузка слайдов и инициализация обработчиков событий при появлении блока в области видимости
     */
    protected startFirstLoadObserving()
    {
        let observer = new IntersectionObserver(
            (entries, observer) => {
                Array.prototype.forEach.call(entries, (entry) => {
                    if (entry.isIntersecting) {
                        observer.disconnect();

                        this.loadSlides(0, this.loadAll ? this.slidesCount : this.getVisbileSlidesCount());
                        this.appendEventListener();

                        triggerCustomEvent(this.root, 'slider.after.init', {
                            detail: {slider: this}
                        });
                    }
                });
            },
            {threshold: [0.5]}
        );

        observer.observe(this.root);
    }

    protected adaptHeight()
    {
        let slidesContainer = this.root.getElementsByClassName('slider__slides')[0] as HTMLElement;
        let maxHeight = 0;

        slidesContainer.childNodes.forEach(node => {
            let slide = node.nextSibling as HTMLElement;
            
            if (slide !== null && slide.scrollHeight > maxHeight) {
                maxHeight = slide.scrollHeight;
            }
        });

        slidesContainer.style.height = maxHeight + 'px';
    }

    protected loadSlides(offset: number, count: number)
    {
        if (!this.isUpdateNeeded(offset, count)) {
            return;
        }

        let slidesContainer = this.root.getElementsByClassName('slider__slides')[0] as HTMLElement;
        let data = new FormData();
        
        data.append('offset', offset.toString());
        data.append('count', count.toString());
        data.append('key', slidesContainer.dataset.sliderId);

        xhr.post('/request/load_slider_items/', data).then(rsp => {
            let items = rsp.data.items;
            if (!(items instanceof Array) || items.length === 0) {
                return;
            }

            for (let slideIdx = offset, dataIdx = 0; slideIdx < offset + count; slideIdx++, dataIdx++) {
                this.root.querySelectorAll('.item-stub[data-slide-id="' + slideIdx + '"]').forEach(slide => {
                    slide.innerHTML = items[dataIdx];
                    slide.classList.remove('item-stub');
                });
            }

            triggerCustomEvent(this.root, 'slider.after.loading', {
                detail: {offset: offset, count: count}
            });
        });
    }

    protected isUpdateNeeded(offset: number, count: number): boolean
    {
        let isUpdateNeeded = false;

        for (let i = offset; i < offset + count; i++) {
            isUpdateNeeded = isUpdateNeeded
                || this.root.querySelectorAll('.item-stub[data-slide-id="' + i + '"]').length > 0;
        }

        return isUpdateNeeded;
    }

    protected getVisbileSlidesCount(): number
    {
        return this.slider.settings.perView;
    }
}
